import React, { useState } from 'react';
import { SmartEstimateOptionCard } from './SmartEstimateOptionCard';
import { PURCHASE_PRICE_MIN as MIN_PRICE, PURCHASE_PRICE_MAX as MAX_PRICE } from '../constants/priceBounds';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Pre-approval SmartEstimate preview. Mounts the REAL SmartEstimateOptionCard
// (same component the approved hero uses) driven by a borrower-entered home
// price, so the numbers match the genuine post-approval estimate. Mirrors
// HeroApproved Phase 1's priceInput → override_purchase_price flow, minus the
// max_approved_price cap, with illustrative rate/down defaults.

// Illustrative note rate for the pre-approval preview (≈ prime + 2). This is the
// one number that sets a pre-approval expectation — confirm/adjust with Jesse.
export const ILLUSTRATIVE_NOTE_RATE = 8.75;

const DEFAULT_DOWN_PCT = 10; // program minimum

const fmtMoney = (digits) => (digits ? Number(digits).toLocaleString() : '');

// ⚠️ terms IS ACCEPTED AS A PROP AND FETCHED WHEN ABSENT. This component mounts
// in two places — inside the approved hero, where Dashboard already holds the
// /api/fee-schedule payload, and inside the standalone calculator modal, which
// is a different component with no access to it. Requiring the prop would have
// left the modal silently rendering the fallback percentages.
export default function SmartEstimatePreview({ terms: termsProp }) {
  const [fetchedTerms, setFetchedTerms] = React.useState(null);
  // 🔑 THE APPRAISAL FEE AND CARRYOVER FROM THE SAME PAYLOAD (2026-10-15): this preview passed
  // neither, so its card always showed a $400 default while the LE disclosed the real price.
  const [fees, setFees] = React.useState({ appraisalFee: null, recordingFee: null, creditReportFee: null, titleLines: null });
  React.useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/fee-schedule`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d) return;
        if (!termsProp && d.smartEstimate) setFetchedTerms(d.smartEstimate);
        setFees({ appraisalFee: d.appraisalFee, recordingFee: d.recordingFee, creditReportFee: d.creditReportFee, titleLines: d.titleLines, titleComputed: d.titleComputed });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [termsProp]);
  const terms = termsProp || fetchedTerms;
  const [phase, setPhase] = useState(1);
  const [priceInput, setPriceInput] = useState('400000');
  const [downPct, setDownPct] = useState(DEFAULT_DOWN_PCT);
  const [priceError, setPriceError] = useState('');
  const [chosenPrice, setChosenPrice] = useState(0);

  const handlePriceChange = (raw) => {
    const digits = String(raw).replace(/[^0-9]/g, '');
    setPriceInput(digits);
    if (priceError) setPriceError('');
  };

  const validate = () => {
    const n = Number(priceInput);
    if (!priceInput || isNaN(n)) { setPriceError('Enter a home price to preview your estimate.'); return false; }
    if (n < MIN_PRICE) { setPriceError(`Minimum home price is $${MIN_PRICE.toLocaleString()}.`); return false; }
    if (n > MAX_PRICE) { setPriceError(`Maximum home price is $${MAX_PRICE.toLocaleString()}.`); return false; }
    setPriceError('');
    return true;
  };

  const handleShowOptions = () => {
    if (!validate()) return;
    setChosenPrice(Number(priceInput));
    setPhase(2);
  };

  // The est payload the real cards consume — the entered price becomes the
  // Doorly acquisition price (override_purchase_price), exactly as in
  // HeroApproved. Illustrative rate + program-min down fill the approval-only
  // fields the card would otherwise read from the approved SmartEstimate.
  const est = {
    override_purchase_price: chosenPrice,
    down_payment_pct: Number(downPct) || DEFAULT_DOWN_PCT,
    approved_interest_rate: ILLUSTRATIVE_NOTE_RATE,
  };
  const noop = () => {};

  return (
    <div>
      {/* Intro header */}
      <div className="mb-3">
        <h3 className="text-lg font-bold text-black">See how a Doorly loan works</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          Doorly purchases the home, then resells it to you. Here's what your estimate looks like.
        </p>
      </div>

      {/* Illustrative banner (mirrors the SmartEstimate FAQ disclaimer) */}
      <div className="mb-4 rounded-xl px-4 py-3" style={{ background: '#eff3fe', border: '1px solid #c5d3f5' }}>
        <p className="text-xs" style={{ color: '#1e40af', lineHeight: 1.5 }}>
          This is an illustrative estimate to show how it works — your actual rate and amounts
          are confirmed when you're approved and select a home.
        </p>
      </div>

      {phase === 1 && (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Home Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Home Price</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={fmtMoney(priceInput)}
                  onChange={(e) => handlePriceChange(e.target.value)}
                  placeholder="400,000"
                  className="w-full h-11 pl-7 pr-3 rounded-lg border text-sm text-gray-900 outline-none"
                  style={{ borderColor: priceError ? '#f87171' : '#e5e7eb' }}
                />
              </div>
              {priceError && <p className="text-red-500 text-xs mt-1.5">{priceError}</p>}
              {!priceError && <p className="text-xs text-gray-400 mt-1.5">$150,000 and up</p>}
            </div>

            {/* Down Payment % */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Down Payment (%)
              </label>
              <div className="relative">
                <input
                  type="number"
                  min={DEFAULT_DOWN_PCT}
                  max={50}
                  step={1}
                  value={downPct}
                  onChange={(e) => setDownPct(e.target.value)}
                  className="w-full h-11 pl-3 pr-7 rounded-lg border text-sm text-gray-900 outline-none"
                  style={{ borderColor: '#e5e7eb' }}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">%</span>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">10% minimum</p>
            </div>
          </div>

          <button
            onClick={handleShowOptions}
            className="mt-5 w-full h-11 rounded-xl text-sm font-semibold text-white"
            style={{ backgroundColor: '#3a6ad9' }}
          >
            See My Estimate →
          </button>
        </div>
      )}

      {phase === 2 && (
        <div>
          {/* Chosen inputs + change */}
          <div className="flex items-center justify-between mb-4 rounded-xl px-4 py-2.5" style={{ background: '#f9fafb', border: '1px solid #f3f4f6' }}>
            <span className="text-xs text-gray-600">
              Home price <strong className="text-gray-900">${chosenPrice.toLocaleString()}</strong>
              <span className="text-gray-400"> · </span>
              {est.down_payment_pct}% down
            </span>
            <button onClick={() => setPhase(1)} className="text-xs font-semibold" style={{ color: '#3a6ad9' }}>
              Change
            </button>
          </div>

          {/* The REAL SmartEstimate card — the same component the dashboard
              mounts, so a borrower sees pre-approval exactly what they will see
              after. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SmartEstimateOptionCard est={est} terms={terms} onSelect={noop} showSelect={false}
              appraisalFee={fees.appraisalFee} recordingFee={fees.recordingFee}
              creditReportFee={fees.creditReportFee} titleLines={fees.titleLines} titleComputed={fees.titleComputed} />
          </div>
        </div>
      )}
    </div>
  );
}
